import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Eye, EyeOff, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const CATEGORIES = ["Web Development", "Graphic Design", "Social Media", "Digital Marketing"];
const EMPTY = { title: "", category: "Web Development", description: "", cover_image: "", gallery: "", objectives: "", deliverables: "", case_study: "", project_url: "", published: false, order: 0 };
const inputCls = "w-full rounded-xl border border-white/10 bg-[#0F1420] px-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-500";

export default function AdminPortfolio() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // null = closed, {id?...} = open
    const [form, setForm] = useState(EMPTY);
    const [busy, setBusy] = useState(false);

    const load = () => {
        setLoading(true);
        api.get("/admin/portfolio").then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e))).finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []);

    const openNew = () => { setForm(EMPTY); setEditing({}); };
    const openEdit = (p) => {
        setForm({
            title: p.title, category: p.category, description: p.description, cover_image: p.cover_image,
            gallery: (p.gallery || []).join("\n"), objectives: (p.objectives || []).join("\n"),
            deliverables: (p.deliverables || []).join("\n"), case_study: (p.case_study || []).join("\n\n"),
            project_url: p.project_url || "",
            published: p.published, order: p.order || 0,
        });
        setEditing(p);
    };

    const lines = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);
    const paragraphs = (s) => s.split(/\n\s*\n/).map((x) => x.trim()).filter(Boolean);

    const save = async (e) => {
        e.preventDefault();
        setBusy(true);
        const payload = {
            title: form.title, category: form.category, description: form.description,
            cover_image: form.cover_image, gallery: lines(form.gallery), objectives: lines(form.objectives),
            deliverables: lines(form.deliverables), case_study: paragraphs(form.case_study),
            project_url: form.project_url,
            published: form.published, order: Number(form.order) || 0,
        };
        try {
            if (editing?.id) await api.put(`/admin/portfolio/${editing.id}`, payload);
            else await api.post("/admin/portfolio", payload);
            toast.success(editing?.id ? "Project updated" : "Project created");
            setEditing(null);
            load();
        } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
    };

    const togglePublish = async (p) => {
        try {
            await api.put(`/admin/portfolio/${p.id}`, {
                title: p.title, category: p.category, description: p.description, cover_image: p.cover_image,
                gallery: p.gallery || [], objectives: p.objectives || [], deliverables: p.deliverables || [],
                case_study: p.case_study || [],
                project_url: p.project_url || "", published: !p.published, order: p.order || 0,
            });
            load();
        } catch (e) { toast.error(errMsg(e)); }
    };

    const remove = async (p) => {
        if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
        try {
            await api.delete(`/admin/portfolio/${p.id}`);
            toast.success("Project deleted");
            load();
        } catch (e) { toast.error(errMsg(e)); }
    };

    return (
        <div data-testid="admin-portfolio-page">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                    <h1 className="font-display text-2xl font-bold text-white">Portfolio</h1>
                    <p className="mt-1 text-sm text-slate-400">Add, edit, publish, reorder, and remove projects.</p>
                </div>
                <button onClick={openNew} data-testid="admin-portfolio-new-button"
                    className="inline-flex items-center gap-2 rounded-full bg-violet-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-violet-500">
                    <Plus className="h-4 w-4" /> New Project
                </button>
            </div>

            <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {loading && <p className="col-span-full py-10 text-center text-sm text-slate-500">Loading…</p>}
                {!loading && items.length === 0 && (
                    <p className="col-span-full rounded-2xl border border-white/10 bg-hive-card p-10 text-center text-sm text-slate-500" data-testid="admin-portfolio-empty">
                        No projects yet. Create your first one.
                    </p>
                )}
                {items.map((p) => (
                    <div key={p.id} className="overflow-hidden rounded-2xl border border-white/10 bg-hive-card" data-testid={`admin-project-${p.slug}`}>
                        <div className="relative aspect-[16/9]">
                            {p.cover_image
                                ? <img src={p.cover_image} alt="" className="h-full w-full object-cover" />
                                : <div className="flex h-full items-center justify-center bg-[#0F1420] text-xs text-slate-600">No cover image</div>}
                            <div className="absolute left-3 top-3 flex gap-2">
                                {p.is_sample && <span className="rounded-full border border-amber-400/40 bg-black/70 px-2.5 py-1 font-mono2 text-[9px] uppercase tracking-widest text-amber-300">Concept</span>}
                                <span className={`rounded-full border px-2.5 py-1 font-mono2 text-[9px] uppercase tracking-widest ${p.published ? "border-emerald-400/40 bg-black/70 text-emerald-300" : "border-slate-500/40 bg-black/70 text-slate-400"}`}>
                                    {p.published ? "Published" : "Draft"}
                                </span>
                            </div>
                        </div>
                        <div className="p-5">
                            <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-cyan-400">{p.category}</p>
                            <h2 className="mt-1.5 font-display text-sm font-bold text-white leading-snug">{p.title}</h2>
                            <div className="mt-4 flex items-center gap-2">
                                <button onClick={() => openEdit(p)} data-testid={`admin-project-edit-${p.slug}`} aria-label="Edit"
                                    className="rounded-lg border border-white/10 p-2 text-slate-300 hover:border-violet-500/50 hover:text-white"><Pencil className="h-3.5 w-3.5" /></button>
                                <button onClick={() => togglePublish(p)} data-testid={`admin-project-publish-${p.slug}`} aria-label={p.published ? "Unpublish" : "Publish"}
                                    className="rounded-lg border border-white/10 p-2 text-slate-300 hover:border-emerald-500/50 hover:text-white">
                                    {p.published ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                                </button>
                                <button onClick={() => remove(p)} data-testid={`admin-project-delete-${p.slug}`} aria-label="Delete"
                                    className="rounded-lg border border-white/10 p-2 text-slate-300 hover:border-rose-500/50 hover:text-rose-300"><Trash2 className="h-3.5 w-3.5" /></button>
                                <span className="ml-auto font-mono2 text-[10px] text-slate-600">order {p.order ?? 0}</span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {editing !== null && (
                <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4 backdrop-blur-sm" data-testid="admin-portfolio-editor">
                    <div className="my-8 w-full max-w-2xl rounded-2xl border border-white/10 bg-hive-card p-7">
                        <div className="flex items-center justify-between">
                            <h2 className="font-display text-lg font-bold text-white">{editing.id ? "Edit Project" : "New Project"}</h2>
                            <button onClick={() => setEditing(null)} aria-label="Close editor" data-testid="admin-portfolio-editor-close"
                                className="rounded-lg p-2 text-slate-400 hover:text-white"><X className="h-5 w-5" /></button>
                        </div>
                        <form onSubmit={save} className="mt-6 grid gap-4 sm:grid-cols-2">
                            <div className="sm:col-span-2">
                                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400" htmlFor="pf-title">Title *</label>
                                <input id="pf-title" required data-testid="portfolio-form-title" className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                            </div>
                            <div>
                                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400" htmlFor="pf-category">Category *</label>
                                <select id="pf-category" data-testid="portfolio-form-category" className={inputCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400" htmlFor="pf-order">Display Order</label>
                                <input id="pf-order" type="number" data-testid="portfolio-form-order" className={inputCls} value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} />
                            </div>
                            <div className="sm:col-span-2">
                                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400" htmlFor="pf-cover">Cover Image URL</label>
                                <input id="pf-cover" data-testid="portfolio-form-cover" className={inputCls} placeholder="https://…" value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} />
                            </div>
                            <div className="sm:col-span-2">
                                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400" htmlFor="pf-desc">Description</label>
                                <textarea id="pf-desc" rows={3} data-testid="portfolio-form-description" className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                            </div>
                            <div className="sm:col-span-2">
                                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400" htmlFor="pf-casestudy">Case Study Narrative (blank line between paragraphs)</label>
                                <textarea id="pf-casestudy" rows={5} data-testid="portfolio-form-case-study" className={inputCls} value={form.case_study} onChange={(e) => setForm({ ...form, case_study: e.target.value })} />
                            </div>
                            <div>
                                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400" htmlFor="pf-objectives">Objectives (one per line)</label>
                                <textarea id="pf-objectives" rows={3} data-testid="portfolio-form-objectives" className={inputCls} value={form.objectives} onChange={(e) => setForm({ ...form, objectives: e.target.value })} />
                            </div>
                            <div>
                                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400" htmlFor="pf-deliverables">Deliverables (one per line)</label>
                                <textarea id="pf-deliverables" rows={3} data-testid="portfolio-form-deliverables" className={inputCls} value={form.deliverables} onChange={(e) => setForm({ ...form, deliverables: e.target.value })} />
                            </div>
                            <div>
                                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400" htmlFor="pf-gallery">Gallery Image URLs (one per line)</label>
                                <textarea id="pf-gallery" rows={3} data-testid="portfolio-form-gallery" className={inputCls} value={form.gallery} onChange={(e) => setForm({ ...form, gallery: e.target.value })} />
                            </div>
                            <div>
                                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400" htmlFor="pf-url">Project URL (optional)</label>
                                <input id="pf-url" data-testid="portfolio-form-url" className={inputCls} placeholder="https://…" value={form.project_url} onChange={(e) => setForm({ ...form, project_url: e.target.value })} />
                            </div>
                            <label className="flex items-center gap-3 sm:col-span-2" htmlFor="pf-published">
                                <input id="pf-published" type="checkbox" data-testid="portfolio-form-published" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })}
                                    className="h-4 w-4 rounded accent-violet-600" />
                                <span className="text-sm text-slate-300">Published (visible on the public site)</span>
                            </label>
                            <div className="flex gap-3 sm:col-span-2">
                                <button type="submit" disabled={busy} data-testid="portfolio-form-save"
                                    className="rounded-full bg-violet-600 px-7 py-3 text-sm font-semibold text-white hover:bg-violet-500 disabled:opacity-60">
                                    {busy ? "Saving…" : "Save Project"}
                                </button>
                                <button type="button" onClick={() => setEditing(null)} className="rounded-full border border-white/15 px-7 py-3 text-sm font-semibold text-slate-300 hover:text-white">
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
